/**
 * Announcement Bar – ping-pong slider & responsive DOM moves
 *
 * Slides right→left to show the next announcement, then left→right
 * to go back, creating a back-and-forth (ping-pong) effect.
 */
if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();

      this.slider = this.querySelector('.announcement__slider');
      this.slides = this.slider?.querySelectorAll('.announcement__text') ?? [];
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');

      this.currentIndex = 0;
      this.direction = 1;           // 1 = forward (right→left), -1 = backward (left→right)
      this.isPaused = false;
      this.timerId = null;

      this._onBreakpoint = this._onBreakpoint.bind(this);

      this._initSlider();
      this._bindEvents();
      this._moveLinks();
      this._moveLocalization();
    }

    disconnectedCallback() {
      clearTimeout(this.timerId);
      window.removeEventListener('on:breakpoint-change', this._onBreakpoint);
    }

    /* ── Slider ─────────────────────────────────────── */

    _initSlider() {
      if (!this.slider || this.slides.length === 0) return;
      this.slides[0].classList.add('is-visible');

      if (this.slides.length > 1) {
        // Apply initial transform to position all slides side by side
        this._updateTransforms();
        this._scheduleNext();
      }
    }

    _scheduleNext() {
      this.timerId = setTimeout(() => {
        if (!this.isPaused) this._advance();
        this._scheduleNext();
      }, 3000);
    }

    _advance() {
      const prev = this.currentIndex;

      // Ping-pong: reverse direction at boundaries
      if (this.currentIndex + this.direction >= this.slides.length) {
        this.direction = -1;
      } else if (this.currentIndex + this.direction < 0) {
        this.direction = 1;
      }

      this.currentIndex += this.direction;
      this._updateTransforms();

      this.slides[this.currentIndex].classList.add('is-visible');
      if (prev !== this.currentIndex) {
        this.slides[prev].classList.remove('is-visible');
      }
    }

    _updateTransforms() {
      // Shift all slides by -currentIndex * 100% so the active one is visible
      const offset = -this.currentIndex * 100;
      for (const slide of this.slides) {
        slide.style.transform = `translateX(${offset}%)`;
      }
    }

    /* ── Events ─────────────────────────────────────── */

    _bindEvents() {
      if (this.slider && this.slides.length > 1) {
        this.slider.addEventListener('mouseenter', () => { this.isPaused = true; });
        this.slider.addEventListener('mouseleave', () => { this.isPaused = false; });
      }

      window.addEventListener('on:breakpoint-change', this._onBreakpoint);
    }

    _onBreakpoint() {
      this._moveLinks();
      this._moveLocalization();
    }

    /* ── DOM moves (mobile nav ↔ announcement bar) ── */

    _moveLinks() {
      if (!this.links.length || !this.menu) return;
      const existing = document.querySelector('.mob__announcement-links');

      if (!theme.mediaMatches.md && !existing) {
        const nav = document.createElement('nav');
        nav.className = 'mob__announcement-links secondary-nav';
        this.links.forEach((link) => {
          nav.insertAdjacentHTML('beforeend',
            `<li><a class="secondary-nav__item" href="${link.href}">${link.innerText}</a></li>`);
        });
        this.menu.appendChild(nav);
      } else if (theme.mediaMatches.md && existing) {
        existing.remove();
      }
    }

    _moveLocalization() {
      if (!this.localization || !this.menu) return;
      const existing = document.querySelector('.mob__localization');

      if (!theme.mediaMatches.md && !existing) {
        const wrapper = document.createElement('div');
        wrapper.className = 'mob__localization';
        wrapper.appendChild(this.localization.firstElementChild);
        this.menu.appendChild(wrapper);
      } else if (theme.mediaMatches.md && existing) {
        this.localization.appendChild(existing.firstElementChild);
        existing.remove();
      }
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}
